import React, { useState, useEffect } from "react";
import { PageTitle } from "../../components";
import {
  Table,
  Space,
  Button,
  Form,
  Input,
  Modal,
  DatePicker,
  message,
  InputNumber,
  Select,
  Checkbox
} from "antd";
import type { ColumnsType } from "antd/lib/table";
import { Link, Outlet } from "react-router-dom";
import {
  MinusCircleOutlined,
  PlusOutlined,
  UploadOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { checkOnlyNumbers } from "../../_helpers/utils";
import { useSelector } from "react-redux";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";
import { competitionsApi, usersApi } from "../../_helpers/apiService";

const { Option } = Select;

interface DataType {
  key: string;
  name: string;
  date: string;
}

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 }
};

const tailLayout = {
  wrapperCol: { offset: 6, span: 18 }
};

const columns: ColumnsType<DataType> = [
  {
    title: "№",
    dataIndex: "id",
    key: "id",
    width: 80,
    render: (text, row, index) => <>{index + 1}</>
  },
  {
    title: "Нэр",
    dataIndex: "name",
    key: "name",
    width: 250,
    render: (item, record) => (
      <Space size="middle">
        <Link to={`/competitions/${record.key}`} key={record.key}>
          {item}
        </Link>
      </Space>
    )
  },
  {
    title: "Өдөр",
    dataIndex: "start_date",
    key: "start_date",
    width: 120,
    render: (item) => {
      return <span>{dayjs(item).format("YYYY-MM-DD")}</span>;
    }
  },
  {
    title: "Төлөв",
    dataIndex: "status",
    key: "status",
    width: 120,
    render: (item) => {
      if (item === "open") return "Нээлттэй";
      return <span>Дууссан</span>;
    }
  }
];

function Competitions() {
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [referees, setReferees] = useState<any[]>([]);
  const [data, setData] = useState<DataType[]>([]);
  const [oldData, setOldData] = useState<DataType[]>([]);
  const [selectedRefs, setSelectedRefs] = useState<any[]>([]);
  const [searchKey, setSearchKey] = useState("displayName");
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const userData = useSelector((state: any) => state.authentication.userData);

  useEffect(() => {
    fetchReferees();
    fetchCompetitions();
  }, []);

  const fetchReferees = async () => {
    try {
      const response = await usersApi.getAll({
        role: 'referee',
        limit: 1000
      });

      const refereesArr = response.data.items.map((referee: any) => ({
        ...referee,
        key: referee.id.toString(),
      }));

      setReferees(refereesArr);
    } catch (error: any) {
      console.error("Fetch referees error:", error);
      message.error("Шүүгчдийн мэдээлэл татахад алдаа гарлаа");
    }
  };

  const fetchCompetitions = async () => {
    setLoading(true);
    try {
      const params: any = {
        club_id: CURRENT_CLUB_ID,
        limit: 1000
      };

      // If user is a referee, only fetch competitions where they are assigned
      if (userData.role === 'referee') {
        params.referee_id = userData.id;
      }

      const response = await competitionsApi.getAll(params);

      let comps = response.data.items.map((comp: any) => ({
        ...comp,
        key: comp.id.toString(),
      }));

      // Additional client-side filtering for referees
      // Check both main referee (referee_id) and zurkhai referees (referees_ids)
      if (userData.role === 'referee') {
        comps = comps.filter((comp: any) => {
          return comp.referee_id === userData.id ||
                 (comp.referees_ids && comp.referees_ids.includes(userData.id));
        });
      }

      setData(comps);
      setOldData(comps);
      setLoading(false);
    } catch (error: any) {
      console.error("Fetch competitions error:", error);
      message.error("Тэмцээний мэдээлэл татахад алдаа гарлаа");
      setLoading(false);
    }
  };

  const onFinish = () => {
    setLoading(true);
    // Filter locally for simplicity
    const filtered = oldData.filter((item: any) => {
      return item.name && item.name.toLowerCase().includes(searchValue?.toLowerCase() || '');
    });
    setData(filtered);
    setLoading(false);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        const zurhai = values.zurhai;
        if (!checkOnlyNumbers(zurhai)) {
          alert("Зурхайн тоо буруу байна");
          return;
        }
        if (!checkOnlyNumbers(values.fee)) {
          alert("Төлбөрийн хэмжээ буруу байна");
          return;
        }
        setConfirmLoading(true);

        try {
          // Convert dates to ISO strings
          const startDate = dayjs(values.startDate).toISOString();
          const endDate = dayjs(values.endDate).toISOString();
          const endRegisterDate = dayjs(values.endRegisterDate).toISOString();

          // Extract referee IDs from the referees array
          const refereesIds = values.referees.map((referee: any) => {
            // referee.refereeId is already the user ID (key)
            return parseInt(referee.refereeId);
          });

          // Main referee ID
          const refereeId = parseInt(values.referee);

          await competitionsApi.create({
            club_id: CURRENT_CLUB_ID,
            name: values.name,
            location: values.location,
            start_date: startDate,
            end_date: endDate,
            end_register_date: endRegisterDate,
            detail_link: values.detailLink || null,
            fee: parseInt(values.fee),
            guidance: values.guidance || null,
            safety: values.safety || null,
            is_rank: values.isRank || false,
            referee_id: refereeId,
            referees_ids: refereesIds,
            status: "open",
            zurhai: zurhai,
            onog_ue: parseInt(values.onogUe)
          });

          setConfirmLoading(false);
          setVisible(false);
          form.resetFields();
          message.success(`"${values.name}" тэмцээн амжилттай үүслээ.`);
          fetchCompetitions(); // Refresh the list
        } catch (error: any) {
          setConfirmLoading(false);
          console.error("Create competition error:", error);
          const errorMessage = error.response?.data?.detail || "Тэмцээн нэмэхэд алдаа гарлаа";
          message.error(errorMessage);
        }
      })
      .catch((info) => {
        setConfirmLoading(false);
        console.log("Validate Failed:", info);
      });
  };

  const clearSearchForm = () => {
    searchForm.resetFields();
    setSearchValue(null);
    setData(oldData);
  };

  const onCompFinish = () => {
    alert("Finish");
  };

  function hasDuplicates(data: any) {
    const refereeIds = new Set();

    for (let i = 0; i < data.length; i++) {
      const item = data[i];

      // Check if the current item has a refereeId property
      if (item?.refereeId) {
        const refereeId = item.refereeId;

        // If the refereeId is already in the Set, it's a duplicate
        if (refereeIds.has(refereeId)) {
          return true;
        }

        // Add the refereeId to the Set
        refereeIds.add(refereeId);
      }
    }

    // No duplicates found
    return false;
  }

  const checkRefs = (_: any, value: any) => {
    if (!hasDuplicates(value)) {
      return Promise.resolve();
    }
    return Promise.reject(
      new Error("Нэг шүүгчийг олон зурхай дээр сонгох боломжгүй!")
    );
  };

  return (
    <>
      <PageTitle
        title="Тэмцээнүүд"
        description="Тэмцээний жагсаалт"
        right={
          userData.role !== "referee" && userData.role !== "leader" ? (
            <Button
              shape="round"
              onClick={() => setVisible(true)}
              icon={<PlusOutlined />}
              size="large"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              Тэмцээн нэмэх
            </Button>
          ) : null
        }
      />
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-8">
                      <Form
                        form={searchForm}
                        layout="inline"
                        style={{ marginBottom: 10 }}
                        initialValues={{ type: searchKey }}
                      >
                        <Form.Item name="name" label="Тэмцээний нэр">
                          <Input
                            onChange={(e: any) =>
                              setSearchValue(e.target.value)
                            }
                          />
                        </Form.Item>
                        <Form.Item {...tailLayout}>
                          <Button type="primary" onClick={() => onFinish()}>
                            Хайх
                          </Button>
                        </Form.Item>
                        <Form.Item {...tailLayout}>
                          <Button
                            type="dashed"
                            onClick={() => clearSearchForm()}
                          >
                            Арилгах
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <Table
                      columns={columns}
                      dataSource={data}
                      loading={loading}
                      rowKey={(item) => item.key}
                      scroll={{ x: 'max-content' }}
                    />
                  </div>

                  {/* <Pagination
                    current={currentPage}
                    totalPages={totalPages}
                    prevAction={prevPage}
                    nextAction={nextPage}
                    changeAction={(num) => changePage(num)}
                  /> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Тэмцээн нэмэх"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Нэмэх"
        cancelText="Болих"
        width={720}
        confirmLoading={confirmLoading}
      >
        <Form
          {...formItemLayout}
          form={form}
          name="register_competition"
          initialValues={{
            detailLink: null,
            guidance: null,
            safety: null,
            referees: []
          }}
        >
          <Form.Item
            label="Улсын чанартай эсэх:"
            name="isRank"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
          <Form.Item
            label="Нэр"
            name="name"
            rules={[{ required: true, message: "Нэр оруулна уу!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Байршил"
            name="location"
            rules={[{ required: true, message: "Байршил оруулна уу!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Эхлэх огноо"
            name="startDate"
            rules={[{ required: true, message: "Эхлэх огноо сонгоно уу!" }]}
          >
            <DatePicker />
          </Form.Item>
          <Form.Item
            label="Дуусах огноо"
            name="endDate"
            rules={[{ required: true, message: "Дуусах огноо сонгоно уу!" }]}
          >
            <DatePicker />
          </Form.Item>
          <Form.Item
            label="Бүртгэл дуусах огноо"
            name="endRegisterDate"
            rules={[
              { required: true, message: "Бүртгэл дуусах огноо сонгоно уу!" }
            ]}
          >
            <DatePicker />
          </Form.Item>
          <Form.Item
            label="Зурхайн тоо"
            name="zurhai"
            rules={[{ required: true, message: "Зурхайн тоо оруулна уу!" }]}
          >
            <InputNumber min={1} />
          </Form.Item>
          <Form.Item
            label="Оног үе"
            name="onogUe"
            rules={[{ required: true, message: "Оног үе оруулна уу!" }]}
          >
            <InputNumber min={1} />
          </Form.Item>
          <Form.List
            name="referees"
            rules={[
              {
                validator: async (_, refers) => checkRefs(_, refers)
              }
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field, index) => (
                  <Form.Item
                    noStyle
                    key={field.key}
                    shouldUpdate={(prevValues, curValues) =>
                      prevValues.zurhai !== curValues.zurhai
                    }
                  >
                    {() => (
                      <Space.Compact block>
                        <Form.Item
                          style={{ width: "100%" }}
                          label={`#${index + 1} зурхайн шүүгч:`}
                          name={[field.name, `refereeId`]}
                          rules={[
                            {
                              required: true,
                              message: "Зурхайн шүүгч сонгоно уу!"
                            }
                          ]}
                        >
                          <Select placeholder="Зурхайн шүүгч сонгох" allowClear>
                            {referees.map((referee) => (
                              <Option key={referee.key} value={referee.key}>
                                {referee.last_name} {referee.first_name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Space align="center" style={{ height: 30 }}>
                          <MinusCircleOutlined
                            onClick={() => remove(field.name)}
                          />
                        </Space>
                      </Space.Compact>
                    )}
                  </Form.Item>
                ))}

                <Form.Item
                  wrapperCol={{
                    span: 14,
                    offset: 6
                  }}
                  style={{ width: "100%" }}
                >
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Зурхай нэмэх
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item
            label="Ерөнхий шүүгч"
            name={"referee"}
            rules={[{ required: true, message: "Ерөнхий шүүгч оруулна уу!" }]}
          >
            <Select placeholder="Ерөнхий шүүгч сонгох" allowClear>
              {referees.map((referee) => (
                <Option key={referee.key} value={referee.key}>
                  {referee.last_name} {referee.first_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Төлбөр"
            name="fee"
            rules={[{ required: true, message: "Төлбөр оруулна уу!" }]}
          >
            <InputNumber />
          </Form.Item>
          <Form.Item label="Линк" name="detailLink">
            <Input />
          </Form.Item>
          <Form.Item label="Удирдамж" name="guidance">
            <Input />
          </Form.Item>
          <Form.Item label="Аюулгүй ажиллагаа" name="safety">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
export default Competitions;
